import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { layout } from "@/constants";
import Modal from "./Modal";
import Text from "./Text";
import TextButton from "./TextButton";
import TextInput from "./TextInput";

/**
 * Props for the ConfirmDeleteDialog component
 */
interface ConfirmDeleteDialogProps {
  /** Whether the dialog is visible */
  visible: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message body */
  message: string;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** If set, user must type this text to enable the Delete button */
  confirmText?: string;
}

/**
 * Reusable confirmation dialog for delete actions
 * Shows a modal with title, message, and Cancel/Delete buttons
 */
export default function ConfirmDeleteDialog({
  visible,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText
}: ConfirmDeleteDialogProps) {
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    if (!visible) setTypedText("");
  }, [visible]);

  const isConfirmDisabled = confirmText ? typedText !== confirmText : false;

  return (
    <Modal
      visible={visible}
      size="dialog"
      title={title}
      onRequestClose={onCancel}
      footer={
        <>
          <TextButton
            buttonText="Cancel"
            variant="secondary"
            onPress={onCancel}
            accessibilityLabel="Cancel"
          />
          <TextButton
            buttonText="Delete"
            variant="destructive"
            onPress={onConfirm}
            disabled={isConfirmDisabled}
            accessibilityLabel="Delete"
          />
        </>
      }
    >
      <Text size="medium" color="secondary">
        {message}
      </Text>
      {confirmText && (
        <View style={styles.confirmInputContainer}>
          <Text size="small" color="secondary">
            Type {confirmText} to confirm
          </Text>
          <TextInput
            value={typedText}
            onChangeText={setTypedText}
            autoCapitalize="characters"
            autoCorrect={false}
          />
        </View>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  confirmInputContainer: {
    marginTop: layout.space.lg,
    gap: layout.space.xs
  }
});
